import { useCallback } from 'react';
import { Container, Graphics, Text } from '@pixi/react';
import * as PIXI from 'pixi.js';

export interface Particle {
  id: number;
  x: number;
  y: number;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  vx: number;
  vy: number;
  color: number;
  size: number;
  alpha: number;
  initialAlpha: number;
  lifetime: number;
  born: number;
  label: string;
  arcHeight: number; // Vertical arc magnitude
  isUpstream: boolean; // true = client→host (arc up), false = host→client (arc down)
}

interface ParticleSystemProps {
  particles: Particle[];
}

export function ParticleSystem({ particles }: ParticleSystemProps) {
  const drawParticle = useCallback(
    (g: PIXI.Graphics, particle: Particle) => {
      g.clear();

      // Outer glow
      g.beginFill(particle.color, particle.alpha * 0.15);
      g.drawCircle(0, 0, particle.size * 3);
      g.endFill();

      // Mid glow
      g.beginFill(particle.color, particle.alpha * 0.3);
      g.drawCircle(0, 0, particle.size * 1.8);
      g.endFill();

      // Core
      g.beginFill(particle.color, particle.alpha);
      g.drawCircle(0, 0, particle.size);
      g.endFill();

      // Bright center
      g.beginFill(0xffffff, particle.alpha * 0.6);
      g.drawCircle(0, 0, particle.size * 0.4);
      g.endFill();
    },
    []
  );

  return (
    <Container>
      {particles.map((particle) => (
        <Container key={particle.id} x={particle.x} y={particle.y}>
          <Graphics draw={(g) => drawParticle(g, particle)} />
          {particle.label && (
            <Text
              text={particle.label}
              x={0}
              y={-particle.size - 14}
              anchor={0.5}
              alpha={particle.alpha}
              style={
                new PIXI.TextStyle({
                  fontFamily: 'SF Mono, Monaco, monospace',
                  fontSize: 11,
                  fontWeight: '600',
                  fill: particle.color,
                  dropShadow: true,
                  dropShadowColor: 0x000000,
                  dropShadowBlur: 3,
                  dropShadowDistance: 0,
                })
              }
            />
          )}
        </Container>
      ))}
    </Container>
  );
}
